import { Box, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalBase } from '../components/ModalBase';
import { useCalculateMetrics } from '../hooks/useCalculateMetrics';
import { getOpenedModel } from '../reducers/data/dataReducer';
import CodeMirror, { ViewUpdate } from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { CodeMirrorStyles, CodeMirrorTheme, FlexBoxInline, InputBaseSx, PrimaryBtnBorderlessSx, SecondaryBtnSx } from '../@shared/templates';
import { Palette, Sizes } from '../@shared/palette';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ContextHelper } from '../components/ContextHelper';
import { dataToJson, parseFileData } from '../@shared/schemaDataParser';
import { onThrowAlert } from '../sagas/dataSaga/dataSagaActions';
import { NotificationModel, NotificationModelType } from '../reducers/main/mainModel';
import { filterHierarchy, getDefaultValueForSchemaItem, getSchemaNamesDictionary } from '../@shared/schemaUtils';
import { StructureColumnTypeEnum } from '../@shared/Models/Models';

export const withCalculateMetricsModal = <T extends Record<string, unknown>>(
  Component: React.ComponentType<T>
): React.FC<T> => (props) => {
  const dispatch = useDispatch();
  const { onCalculate, onDataUpdate, onSetSchemaOrdering, isProcessing, metrics, data, schemaOrdering, isOpen, handleClose } = useCalculateMetrics();

  const model = useSelector(getOpenedModel);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const flattenedSchemaItems = React.useMemo(() => {
    if (!model?.schema?.schemaItems) return [];
    const filtered = filterHierarchy(model.schema.schemaItems, [StructureColumnTypeEnum.Feature, StructureColumnTypeEnum.Info]);
    return getSchemaNamesDictionary(filtered)
      .slice()
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [model?.schema]);

  const structure = React.useMemo(() => {
    const valueItem: unknown[] = [];
    flattenedSchemaItems.forEach(({ schemaItem }) => {
      valueItem.push(getDefaultValueForSchemaItem(schemaItem));
    });
    return [valueItem];
  }, [flattenedSchemaItems]);

  const baseOrder = React.useMemo(() => {
    return flattenedSchemaItems.map(x => x.key).join(", ");
  }, [flattenedSchemaItems]);

  const onChangeData = React.useCallback((value: string, _: ViewUpdate) => {
    onDataUpdate(value);
  }, [onDataUpdate]);

  const onCopyBaseOrder = React.useCallback(async () => {
    navigator.clipboard.writeText(baseOrder);
  }, [baseOrder]);

  const handleLoadFile = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const fileContent = e.target?.result as string;
      if (fileContent && flattenedSchemaItems.length > 0) {
        const schemaItemsForParsing = flattenedSchemaItems.map(f => ({
          ...f.schemaItem,
          name: f.key
        }));
        const result = parseFileData(fileContent, schemaItemsForParsing);

        if (result.isValid) {
          onDataUpdate(dataToJson(result.data, true));
          onSetSchemaOrdering(result.schemaOrdering.join(","));
        } else {
          dispatch(onThrowAlert({
            message: result.error || "Failed to parse file. Please ensure it's a valid CSV or JSON schema file.",
            lifeTime: 15000,
            type: NotificationModelType.Error
          } as NotificationModel));
        }
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  }, [dispatch, flattenedSchemaItems, onDataUpdate, onSetSchemaOrdering]);

  React.useEffect(() => {
    if (isOpen) {
      onDataUpdate(JSON.stringify(structure, null, 2));
      onSetSchemaOrdering(baseOrder);
    }
  }, [baseOrder, isOpen, structure, onDataUpdate, onSetSchemaOrdering]);

  return <>
    <ModalBase
      isOpen={isOpen}
      onCancel={handleClose}
      onSave={onCalculate}
      title="Calculate Metrics"
      isProcessing={isProcessing}
      isDisabledSave={isProcessing}
      primaryBtnText="Calculate"
    >
      <Box sx={{ paddingTop: "0px", paddingBottom: "15px" }}>
        <ContextHelper boxSx={{ marginBottom: "5px" }} content={<>The list of item names that define the order of values in each data row. It ensures that every value in data matches the correct feature, target, or info field.</>}>
          <TextField sx={{ ...InputBaseSx }} value={schemaOrdering} onChange={(e) => onSetSchemaOrdering(e.target.value)} fullWidth id="Schema-Ordering-field" size="small" label="Schema Ordering" variant="outlined" />
        </ContextHelper>
        <Box sx={{ ...FlexBoxInline, marginBottom: "10px" }}>
          <Typography sx={{
            color: Palette.textColorSecondary,
            fontSize: Sizes.textSecondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
            Basic order: {baseOrder}
          </Typography>
          <Tooltip title={"Copy"} placement="bottom" arrow>
            <IconButton sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="copy" onClick={onCopyBaseOrder}>
              <ContentCopyIcon sx={{ fontSize: Sizes.textPrimary }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ ...FlexBoxInline }}>
          <Box sx={{ width: "50%" }}>
            <Box sx={{ ...FlexBoxInline, alignItems: "flex-end", justifyContent: "space-between", marginBottom: "0px" }}>
              <ContextHelper content={<>The dataset must be provided as a JSON array of rows. Each row is an array of values, and the values must follow the order defined in Schema Ordering — the first value corresponds to the first field name, the second value to the second field name, and so on.</>} boxSx={{ marginBottom: "5px" }}>
                <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Data:</Typography>
              </ContextHelper>
              <Tooltip title={"Upload CSV or JSON file"} placement="bottom" arrow>
                <IconButton sx={{ ...PrimaryBtnBorderlessSx, fontSize: Sizes.icon, p: "5px", border: "none" }} size="medium" onClick={handleLoadFile}>
                  <UploadFileIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
            <CodeMirror value={data} height="400px" extensions={[json()]} onChange={onChangeData} style={CodeMirrorStyles} theme={CodeMirrorTheme} />
          </Box>
          <Box sx={{ width: "50%", position: "relative" }}>
            <Box sx={{ ...FlexBoxInline, alignItems: "flex-end", justifyContent: "space-between", marginBottom: "0px" }}>
              <ContextHelper content={<>The metrics result returned after calculating predictions against actual target values.</>} boxSx={{ marginBottom: "5px" }}>
                <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Metrics:</Typography>
              </ContextHelper>
            </Box>
            <Box sx={{ position: "relative" }}>
              <CodeMirror readOnly value={metrics?.metrics || ""} height="400px" extensions={[json()]} style={CodeMirrorStyles} theme={CodeMirrorTheme} />
              {isProcessing && <Box sx={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0, background: Palette.btnPrimaryHover }}>
                <CircularProgress sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                  color: Palette.btnPrimary
                }} />
              </Box>}
            </Box>
          </Box>
        </Box>
      </Box>
      <input
        type="file"
        ref={fileInputRef}
        accept=".csv,text/csv,.json,application/json"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        aria-label="Upload CSV or JSON file"
      />
    </ModalBase>
    <Component {...props as T} />
  </>
}